import dayjs, { Dayjs } from "dayjs";
import duration from "dayjs/plugin/duration";

// Estendendo Day.js com os plugins necessários
dayjs.extend(duration);

// Tipos para os parâmetros e retorno
export interface RemainingTimeParams {
  date: string | Date | Dayjs;
  timeFormat?: string;
}

export interface RemainingTimeResult {
  days: number;
  time: string;
  totalMs: number;
}

export interface CountdownController {
  stop: () => void;
}

/**
 * Calcula o tempo restante até uma data específica
 * @param params Objeto com a data alvo e formato de tempo
 * @returns Objeto com dias restantes, tempo formatado e total de milissegundos
 */
const getRemainingTime = ({ date, timeFormat = "HH:mm:ss" }: RemainingTimeParams): RemainingTimeResult => {
  const now = dayjs();
  const targetDate = dayjs(date);

  // Validação da data
  if (!targetDate.isValid()) {
    throw new Error("Data inválida fornecida");
  }

  const diffInMilliseconds = targetDate.diff(now);

  // Caso o tempo já tenha expirado
  if (diffInMilliseconds <= 0) {
    return {
      days: 0,
      time: dayjs.duration(0).format(timeFormat),
      totalMs: 0
    };
  }

  const totalDuration = dayjs.duration(diffInMilliseconds);

  // Calcula dias completos restantes
  const days = Math.floor(totalDuration.asDays());

  // Calcula o restante em milissegundos após remover os dias completos
  const remainingMs = diffInMilliseconds - days * 24 * 60 * 60 * 1000;
  const timePart = dayjs.duration(remainingMs);

  return {
    days,
    time: timePart.format(timeFormat),
    totalMs: diffInMilliseconds
  };
};

/**
 * Inicia uma contagem regressiva em tempo real
 * @param params Objeto com a data alvo e formato de tempo
 * @param callback Função chamada a cada atualização
 * @param interval Intervalo de atualização em milissegundos (padrão: 1000ms)
 * @returns Objeto com função para parar a contagem
 */
const startRealTimeCountdown = (params: RemainingTimeParams, callback: (result: RemainingTimeResult) => void, interval: number = 1000): CountdownController => {
  let isRunning = true;

  const execute = () => {
    if (!isRunning) return;

    try {
      const result = getRemainingTime(params);
      callback(result);

      // Para automaticamente quando o tempo expirar
      if (result.totalMs <= 0) {
        isRunning = false;
      }
    } catch (error) {
      isRunning = false;
      console.error("Erro na contagem regressiva:", error);
    }
  };

  // Executa imediatamente a primeira vez
  execute();

  // Configura o intervalo de atualização
  const intervalId = setInterval(execute, interval);

  // Retorna o controlador para parar a contagem
  return {
    stop: () => {
      isRunning = false;
      clearInterval(intervalId);
    }
  };
};

export { getRemainingTime, startRealTimeCountdown };
